import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';
import { ChangeEvent, ComponentProps, useEffect, useRef, useState } from 'react';
import { Container, ProgressBar, Row } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLocation, useNavigate } from 'react-router-dom';
import PosSettingsIcon from '../../../assets/pos-settings.svg';
import PosSettlementIcon from '../../../assets/pos-settlement.svg';
import POSIcon from '../../../assets/POS.svg';
import BorderedText from '../../../components/BorderedText/BorderedText';
import InfoBlock from '../../../components/BusinessInfo/InfoBlock';
import Input from '../../../components/Input/Input';
import RoundedCheckbox from '../../../components/Input/RadioButton';
import Select, { BooleanishOptions } from '../../../components/Input/Select';
import Switch from '../../../components/Input/Switch';
import NavigationFooter from '../../../components/NavigationFooter/NavigationFooter';
import {
  deleteElectronicInvoice,
  getCities,
  getElectronicInvoice,
  getInsurances,
  getStates,
  getTerminals,
  getTerminalsPrice,
  saveElectronicInvoice,
  saveTerminalInformation,
} from '../../../services/commerce.service';
import {
  BusinessTableColumn,
  BusinessTableRow,
  City,
  CustomTableChunk,
  InfoBlockField,
  State,
} from '../../../types/add_commerce.types';
import { ECommerceProcessorEnum } from '../../../types/enums/ecommerce-processor.enum';
import { InstitutionEnum } from '../../../types/enums/institution.enum';
import { ShippingTypeEnum } from '../../../types/enums/shipping-type.enum';
import { TafaceTypePlanEnum } from '../../../types/enums/taface-type-plan.enum';
import { Insurance } from '../../../types/terminals/model';
import {
  documentTypes,
  integrationType,
  INTEGRATION_NO,
  INTEGRATION_OTHERS,
  tafaceBusinessType,
  tafaceCompanyType,
  tafaceDigitalCertificate,
  terminalHeadings,
} from '../../../utils/data';
import {
  clearElectronicInvoiceCertificateBlockFields,
  electronicInvoicePayloadHasAnyValue,
  emptyCertificateRegistrationFields,
  mapElectronicInvoiceApiToForm,
  normalizeCertificateDurationFromApi,
  normalizePlanTypeFromApi,
  nullifyCertificateRegistrationFieldsInPayload,
  pickPlanTypeFromElectronicInvoice,
  restoreElectronicInvoiceCertificateBlockFromApi,
} from '../../../utils/electronicInvoiceHelpers';
import { triggerToasts } from '../../../utils/helpers';
import { isInstitution, isZurichChannelAllowed } from '../../../utils/institutionUtils';
import { pagesPaths } from '../../../utils/navigationUtils';
import { hasValue } from '../../../utils/objectUtils';
import { getProgressPercentage } from '../../../utils/progressHelper';
import schemas from '../../../utils/schemas';
import {
  buildTerminalRequestBody,
  getBusinessPromotion,
  getIntegrationTypesValues,
  getTerminalFromValues,
} from '../../../utils/terminalHelpers';
import { AddressCitySelect, AddressStateSelect } from '../Address/AddressSelect';
import PersonalaizedTariffTable from '../Conditions/PersonalaizedTariff';
import ZurichModal from './ZurichModal';

let sameAsMainAddress: boolean = false;

function ElectronicInvoicePlanTypeEffect({ invoicingType }: { invoicingType: string }) {
  const { setFieldValue } = useFormikContext();
  useEffect(() => {
    if (invoicingType !== 'NO_TERMINAL') setFieldValue('planType', null);
  }, [invoicingType, setFieldValue]);
  return null;
}

function CommerceTerminals() {
  const location = useLocation();
  const navigate = useNavigate();
  const localState: any = location.state;
  const [terminals, setTerminals] = useState<CustomTableChunk[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [noPosSaleComments, setNoPosSaleComments] = useState<string>(localState.noPosSaleComments || '');
  const [showZurichModal, setShowZurichModal] = useState<boolean>(false);
  const [insurances, setInsurances] = useState<Array<Insurance>>([]);
  const [showZurichSwitch, setShowZurichSwitch] = useState<boolean>(false);
  const [electronicInvoiceFromApi, setElectronicInvoiceFromApi] = useState<Record<string, unknown> | null>(
    null,
  );
  const [invoiceApiMeta, setInvoiceApiMeta] = useState<{
    electronicInvoicing: boolean;
    digitalCertificateRequest: boolean;
  } | null>(null);
  const [electronicInvoicingTypeFromApi, setElectronicInvoicingTypeFromApi] = useState<string | null>(null);
  const showNadProducts = localState?.nadOnboarding === false ? false : true;

  const formRef = useRef<FormikProps<any> | null>(null);
  const electronicInvoicingFormRef = useRef<FormikProps<any> | null>(null);

  const handleGoBack = () => navigate(pagesPaths.CommerceConditions, { state: { ...localState } });
  const handleSubmit = () => formRef.current?.submitForm();

  const zeroFilteredTerminals = (
    setFieldValue: (field: string, value: any) => void,
    shouldZero: (row: BusinessTableRow) => boolean,
    formCurrentTerminals?: any,
  ) => {
    if (!terminals.length) return;

    const currentQuantityMap = new Map<string, { currency: any; deferred: any }>();
    if (formCurrentTerminals?.[0]?.rows) {
      for (const row of formCurrentTerminals[0].rows as BusinessTableRow[]) {
        currentQuantityMap.set(String(row.id), { currency: row.currency, deferred: row.deferred });
      }
    }

    const updated = terminals.map((chunk) => ({
      ...chunk,
      rows: chunk.rows.map((row) => {
        if (shouldZero(row)) return { ...row, currency: 0, deferred: 0 };
        const current = currentQuantityMap.get(String(row.id));
        return current ? { ...row, currency: current.currency, deferred: current.deferred } : row;
      }),
    }));
    setTerminals(updated);
    setFieldValue('terminals', updated);
  };

  const zeroEposTerminals = (setFieldValue: (field: string, value: any) => void) =>
    zeroFilteredTerminals(setFieldValue, (row) => !!row.electronicInvoicingAssociated);

  const resetTerminalsByType = (
    setFieldValue: (field: string, value: any) => void,
    newType: string,
    formValues?: any,
  ) => {
    if (newType === 'NO_TERMINAL') {
      zeroFilteredTerminals(setFieldValue, (row) => !!row.electronicInvoicingAssociated, formValues);
    } else if (newType === 'GETNET_INTEGRATION') {
      zeroFilteredTerminals(
        setFieldValue,
        (row) => !!row.electronicInvoicingAssociated && !!row.otherIntegration,
        formValues,
      );
    } else if (newType === 'OTHER_INTEGRATION') {
      zeroFilteredTerminals(
        setFieldValue,
        (row) => !!row.electronicInvoicingAssociated && !row.otherIntegration,
        formValues,
      );
    }
  };

  const saveTerminalsInformation = async (values: any) => {
    const integrationTypeValues = values.integrationType.filter((value: number) => value !== 0);

    const effectiveElectronicInvoicing =
      values.integration !== INTEGRATION_NO || values.commercialCenter !== 'NO'
        ? false
        : values.electronicInvoicing;

    const hasNoTerminalElectronicInvoicing =
      effectiveElectronicInvoicing && values.electronicInvoicingType === 'NO_TERMINAL';

    const formattedTerminals = hasNoTerminalElectronicInvoicing
      ? (localState?.terminals || [])
          .filter((t: any) => Number(t.quantity) > 0)
          .map((t: any) => ({
            type: String(t.type),
            quantity: Number(t.quantity),
            UIPrice: t.price,
            delete: true,
          }))
      : getTerminalFromValues(values, localState?.terminals || []);

    const hasAnyTerminalWithQuantity = formattedTerminals.some(
      (terminal: { quantity: number; delete: boolean }) => !terminal.delete && Number(terminal.quantity) > 0,
    );

    if (
      values.commercialCenter === 'NO' &&
      values.integration === INTEGRATION_NO &&
      !values.wantsIntegratedPos &&
      values.posSale === 'true' &&
      !hasAnyTerminalWithQuantity
    ) {
      return triggerToasts('La cantidad de terminales debe ser al menos 1');
    }

    if (noPosSaleComments.length > 512) {
      return triggerToasts('Comentarios demasiado extensos');
    }

    // A stale insurancesIds from a resumed onboarding under a different channel doesn't count
    // as a chosen product if the current channel isn't allowed to contract Zurich.
    const hasChosenInsurance = isZurichChannelAllowed() && values.insurancesIds.length > 0;

    if (
      !values.ecommerce &&
      values.paymentProcessor === '' &&
      values.integration === INTEGRATION_NO &&
      values.posSale === 'false' &&
      !values.wantsIntegratedPos &&
      !hasChosenInsurance &&
      values.commercialCenter === 'NO' &&
      localState.nadOnboarding &&
      !hasNoTerminalElectronicInvoicing
    ) {
      return triggerToasts('Debe elegir algún producto');
    }

    if (values.ecommerce && integrationTypeValues.length === 0 && !values.paymentProcessor) {
      return triggerToasts('Debe elegir alguna pasarela de pago o producto');
    }

    if (effectiveElectronicInvoicing && values.electronicInvoicingType !== 'OTHER_INTEGRATION') {
      const electronicForm = electronicInvoicingFormRef.current;
      if (!electronicForm) return;
      const electronicInvoiceErrors = await electronicForm.validateForm();
      if (electronicInvoiceErrors && Object.keys(electronicInvoiceErrors).length > 0) {
        const errObj = electronicInvoiceErrors as Record<string, unknown>;
        const touchKeys = new Set([...Object.keys(electronicForm.values), ...Object.keys(errObj)]);
        const allTouched = Array.from(touchKeys).reduce(
          (acc, key) => {
            acc[key] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        );
        electronicForm.setTouched(allTouched, false);
        return;
      }
      await electronicForm.submitForm();
    } else if (effectiveElectronicInvoicing && values.electronicInvoicingType === 'OTHER_INTEGRATION') {
      const isUpdate =
        electronicInvoiceFromApi !== null && electronicInvoicePayloadHasAnyValue(electronicInvoiceFromApi);
      const response = await saveElectronicInvoice(
        nullifyCertificateRegistrationFieldsInPayload(
          { type: 'OTHER_INTEGRATION', planType: null } as Record<string, unknown>,
          false,
        ),
        localState.businessId || localState.id,
        isUpdate,
      );
      if (!response.result) {
        return triggerToasts('Error al guardar los datos de facturación electrónica');
      }
    } else if (
      electronicInvoiceFromApi !== null &&
      electronicInvoicePayloadHasAnyValue(electronicInvoiceFromApi)
    ) {
      const businessId = Number(localState.businessId || localState.id);
      const deleteRes = await deleteElectronicInvoice(businessId);
      const deleteFailed =
        deleteRes &&
        typeof deleteRes === 'object' &&
        'status' in deleteRes &&
        (deleteRes as { status: number }).status >= 400;
      if (deleteFailed && (deleteRes as { status: number }).status !== 404) {
        return triggerToasts('Error al eliminar la facturación electrónica');
      }
    }

    setIsLoading(true);

    // Guards against resuming an onboarding whose channel changed after Zurich was contracted:
    // never submit insurancesIds if the current channel isn't allowed to contract Zurich.
    const sanitizedValues = isZurichChannelAllowed() ? values : { ...values, insurancesIds: [] };

    const requestBody = buildTerminalRequestBody({
      values: sanitizedValues,
      localState,
      formattedTerminals,
      integrationTypeValues,
      noPosSaleComments,
    });

    const response = await saveTerminalInformation(requestBody, localState.businessId || localState.id);
    setIsLoading(false);

    if (response.result && response.data) {
      const electronicValues = electronicInvoicingFormRef.current?.values;
      const forwardState: Record<string, unknown> = {
        ...localState,
        ...response.data.businessData,
        electronicInvoicing: effectiveElectronicInvoicing,
        electronicInvoicingType: values.electronicInvoicingType,
        digitalCertificateRequest: values.digitalCertificateRequest,
      };
      if (
        effectiveElectronicInvoicing &&
        values.electronicInvoicingType === 'NO_TERMINAL' &&
        electronicValues?.planType != null
      ) {
        forwardState.planType = electronicValues.planType;
      }
      return navigate(pagesPaths.CommerceCodes, { state: forwardState });
    }
  };

  const fetchInsurances = async () => {
    const responseInsurances = await getInsurances();
    if (Array.isArray(responseInsurances.data)) {
      setInsurances(responseInsurances.data);
      return responseInsurances.data;
    }
  };

  const fetchTerminals = async () => {
    const terminalsBusinessId = localState?.businessId || localState?.id;
    if (!terminalsBusinessId || !Number.isFinite(Number(terminalsBusinessId))) return;
    const responseTerminals = await getTerminals(String(terminalsBusinessId));
    const tableFormatedTerminals: BusinessTableRow[] = await Promise.all(
      responseTerminals.data.map(async (terminal: any) => {
        const preSettedTerminal = localState?.terminals?.length
          ? localState.terminals.find((businessTerminal: any) => +businessTerminal.type === terminal.id)
          : null;

        let price = preSettedTerminal?.price || 0;
        if (preSettedTerminal?.quantity) {
          const priceRes = await getTerminalsPrice(
            terminal.id.toString(),
            preSettedTerminal.quantity.toString(),
            String(terminalsBusinessId),
          );
          if (priceRes?.data?.price != null) price = priceRes.data.price;
        }

        return {
          id: terminal.id.toString(),
          plan: terminal.name,
          fees: terminal.price,
          currency: preSettedTerminal?.quantity || 0,
          deferred: price,
          electronicInvoicingStatus: 'No asociado',
          electronicInvoicingAssociated: Boolean(terminal.epos),
          otherIntegration: Boolean(terminal.otherIntegration),
        };
      }),
    );

    const tableData: CustomTableChunk[] = [{ rows: tableFormatedTerminals }];
    setTerminals(tableData);
  };

  const fetchElectronicInvoice = async () => {
    if (!localState?.id && !localState?.businessId) return;
    const businessId = String(localState.businessId || localState.id);
    const res = await getElectronicInvoice(businessId);
    if (!res || (typeof res.status === 'number' && res.status >= 400)) return;
    if (res.result === false) return;
    const invoice =
      res.data !== undefined && typeof res.data === 'object' && !Array.isArray(res.data)
        ? res.data
        : res.result !== undefined
          ? res.data
          : res;
    if (!invoice || typeof invoice !== 'object' || Array.isArray(invoice)) {
      setElectronicInvoiceFromApi(null);
      setInvoiceApiMeta(null);
      setElectronicInvoicingTypeFromApi(null);
      return;
    }
    const normalized = mapElectronicInvoiceApiToForm(invoice as Record<string, unknown>);
    if (!electronicInvoicePayloadHasAnyValue(normalized)) {
      setElectronicInvoiceFromApi(null);
      setInvoiceApiMeta(null);
      setElectronicInvoicingTypeFromApi(null);
      return;
    }
    setElectronicInvoiceFromApi(normalized);
    setInvoiceApiMeta({
      electronicInvoicing: true,
      digitalCertificateRequest: hasValue(normalized.dgiCode),
    });
    const t = normalized.type;
    setElectronicInvoicingTypeFromApi(typeof t === 'string' ? t : null);
  };

  const validateShowZurichSwitch = async () => {
    var showZurich = false;

    if (isZurichChannelAllowed()) {
      if (!localState.accountTrackingNumber) {
        if (localState.bankAccounts.length > 0) {
          const bankAccount = localState.bankAccounts.find(
            (bankAccount: any) => bankAccount.bankExternalId === '55',
          );
          if (bankAccount) {
            showZurich = true;
          }
        }
      } else {
        showZurich = true;
      }
    }

    var insurances = await fetchInsurances();

    if (showZurich && insurances.length > 0) {
      if (insurances[0].isActive) {
        setShowZurichSwitch(true);
      } else {
        setShowZurichSwitch(false);
      }
    } else {
      setShowZurichSwitch(false);
    }
  };

  useEffect(() => {
    if (!showNadProducts) return;
    void fetchTerminals();
    void fetchElectronicInvoice();
    void fetchInsurances();
    void validateShowZurichSwitch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container className="commerce-terminals">
        <ProgressBar now={getProgressPercentage('CommerceTerminals')} />
        <h1 className="title mb-4">
          <img src={POSIcon} alt="" className="me-3" />
          Productos
        </h1>
        <Formik
          enableReinitialize
          initialValues={{
            ecommerce: localState.commercialCenter ? !!localState.ecommerce : false,
            paymentProcessor: localState?.ecommerce?.processor || '',
            integrationType: localState?.ecommerce?.integrationTypes?.length
              ? getIntegrationTypesValues(localState?.ecommerce?.integrationTypes)
              : [],
            commercialCenter: localState.commercialCenter || 'NO',
            integration: localState.integration ?? INTEGRATION_NO,
            wantsIntegratedPos: localState.wantsIntegratedPos ?? false,
            posSale:
              !!localState.posSale &&
              localState?.electronicInvoicingType !== 'NO_TERMINAL' &&
              electronicInvoicingTypeFromApi !== 'NO_TERMINAL'
                ? 'true'
                : 'false',
            noPosSaleComments: localState.noPosSaleComments || '',
            terminals: localState?.terminals || '',
            settlement: true,
            promotion: getBusinessPromotion(localState),
            shippingType: localState.shippingType || ShippingTypeEnum.NAD,
            withdrawInGetnet: !localState.wantsIntegratedPos ? !localState.shipping : false,
            stateId: localState?.shippingAddress?.city?.state?.id || '',
            cityId: localState?.shippingAddress?.city?.id || '',
            addressStreet: localState?.shippingAddress?.addressStreet || '',
            addressNumber: localState?.shippingAddress?.addressNumber || '',
            addressFlatNumber: localState?.shippingAddress?.addressFlatNumber || '',
            addressSolar: localState?.shippingAddress?.addressSolar || '',
            addressPlot: localState?.shippingAddress?.addressPlot || '',
            deliveryTime: localState?.shippingAddress?.schedule || '',
            phoneNumber: localState?.shippingAddress?.contactPhone || '',
            insurancesIds: localState?.insurances?.length > 0 ? [localState.insurances[0].id] : [],
            electronicInvoicing:
              invoiceApiMeta !== null
                ? invoiceApiMeta.electronicInvoicing || localState?.electronicInvoicing
                : localState?.electronicInvoicing || false,
            electronicInvoicingType:
              electronicInvoicingTypeFromApi ?? localState?.electronicInvoicingType ?? 'GETNET_INTEGRATION',
            digitalCertificateRequest:
              invoiceApiMeta !== null
                ? invoiceApiMeta.digitalCertificateRequest || localState?.digitalCertificateRequest
                : (localState?.digitalCertificateRequest ?? false),
          }}
          onSubmit={saveTerminalsInformation}
          innerRef={(formElement) => (formRef.current = formElement)}
          validationSchema={schemas.CommerceTerminalsSchema}
        >
          {({ setFieldValue, values }) => {
            const showElectronicInvoicing =
              values.integration === INTEGRATION_NO &&
              values.commercialCenter === 'NO' &&
              // To enable electronic invoicing for another institution, add it to InstitutionEnum and list it here
              isInstitution(InstitutionEnum.GETNET, InstitutionEnum.CANAL_5, InstitutionEnum.CANAL_1);
            return (
              <Form className="row">
                {showZurichSwitch && (
                  <>
                    <div className="input d-flex flex-column flex-md-row form-switch col-md-12 align-items-md-center align-items-start">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        name="insurances"
                        id="insurances_switch"
                        onChange={(event) => {
                          if (event.target.checked) {
                            if (insurances.length > 0) {
                              setFieldValue('insurancesIds', [insurances[0].id]);
                            } else {
                              return triggerToasts('Los seguros aún no están cargados. Intenta más tarde.');
                            }
                          } else {
                            setFieldValue('insurancesIds', []);
                          }
                        }}
                        checked={values.insurancesIds?.length ? true : false}
                      />
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                          <label htmlFor="insurances_switch">
                            Agregar protección para ingresos a tan solo ${insurances[0]?.price} por mes
                          </label>
                          <button
                            type="button"
                            className="btn btn-link text-decoration-underline p-0 border-0 bg-transparent"
                            style={{
                              color: '#dc3545',
                              fontSize: '0.9rem',
                              whiteSpace: 'nowrap',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                            onClick={() => setShowZurichModal(true)}
                          >
                            Más información
                          </button>
                        </div>
                        <p className="text-muted small mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                          Oferta válida solo para clientes del Banco Santander. Producto sujeto a aprobación
                          crediticia y condiciones de contratación por Zurich Santander Seguros.
                        </p>
                      </div>
                    </div>
                  </>
                )}
                <Switch
                  htmlFor="ecommerce_switch"
                  id="ecommerce_switch"
                  label="Agregar e-commerce al comercio"
                  handleOnClick={() => {
                    if (values.ecommerce) {
                      setFieldValue('paymentProcessor', '');
                      setFieldValue('integrationType', []);
                    }
                    setFieldValue('ecommerce', !values.ecommerce);
                  }}
                  name="ecommerce"
                  containerClass="col-md-12 mt-0"
                  checked={values.ecommerce}
                />
                {values.ecommerce && (
                  <>
                    <PaymentProcessorGroup />
                    <IntegrationTypeGroup />
                  </>
                )}
                {showNadProducts && (
                  <>
                    {(values.integration !== INTEGRATION_NO || values.commercialCenter !== 'NO') && (
                      <p className="checkbox-label col-12 mt-3 mb-4">
                        Esta gestión está siendo llevada a cabo por un ejecutivo de Getnet
                      </p>
                    )}

                    <Select
                      label="Centro comercial"
                      className="col-md-6"
                      name="commercialCenter"
                      required
                      noPlaceholder
                      handleOnChange={(value) => {
                        if (value !== 'NO') {
                          setFieldValue('wantsIntegratedPos', false);
                          setFieldValue('posSale', 'false');
                          setFieldValue('electronicInvoicing', false);
                        }
                        setFieldValue('commercialCenter', value);
                      }}
                    >
                      <option value="NO">No</option>
                      <option value="Centro Comercial Salto">Centro Comercial Salto</option>
                      <option value="Centro Comercial Paysandú">Centro Comercial Paysandú</option>
                    </Select>
                    <Select
                      label="¿El comercio tiene software de punto de venta?"
                      className="col-md-6"
                      name="integration"
                      required
                      noPlaceholder
                      handleOnChange={(value) => {
                        if (value !== INTEGRATION_NO) {
                          setFieldValue('wantsIntegratedPos', false);
                          setFieldValue('posSale', 'false');
                          setFieldValue('electronicInvoicing', false);
                        }
                        setFieldValue('integration', value);
                      }}
                    >
                      <BooleanishOptions trueValue={INTEGRATION_OTHERS} falseValue={INTEGRATION_NO} />
                    </Select>
                    {showElectronicInvoicing && (
                      <>
                        <Switch
                          htmlFor="electronicInvoicing_switch"
                          id="electronicInvoicing_switch"
                          label="¿Agregar facturación electrónica?"
                          handleOnClick={() => {
                            const newValue = !values.electronicInvoicing;
                            setFieldValue('electronicInvoicing', newValue);
                            if (newValue === true) {
                              setFieldValue(
                                'posSale',
                                values.electronicInvoicingType === 'NO_TERMINAL' ? 'false' : 'true',
                              );
                            } else {
                              zeroEposTerminals(setFieldValue);
                            }
                          }}
                          name="electronicInvoicing"
                          containerClass="col-md-12 mt-0"
                          checked={values.electronicInvoicing}
                        />
                        {values.electronicInvoicing && (
                          <>
                            <div className="mb-3">
                              <h3 className="section-title mt-4 mb-4">FACTURACION ELECTRONICA</h3>
                              <div className="d-flex gap-4">
                                <RoundedCheckbox
                                  name="electronicInvoicingType"
                                  label="Integración Getnet(ePOS)"
                                  onChange={(value) => {
                                    setFieldValue('electronicInvoicingType', value);
                                    setFieldValue('posSale', 'true');
                                    resetTerminalsByType(
                                      setFieldValue,
                                      'GETNET_INTEGRATION',
                                      values.terminals,
                                    );
                                  }}
                                  setFieldValue={setFieldValue}
                                  value="GETNET_INTEGRATION"
                                  customClass="px-0"
                                  currentComboCheckboxValue={values.electronicInvoicingType}
                                />
                                <RoundedCheckbox
                                  name="electronicInvoicingType"
                                  label="Otra Integracion(ePOS)"
                                  onChange={(value) => {
                                    setFieldValue('electronicInvoicingType', value);
                                    setFieldValue('posSale', 'true');
                                    resetTerminalsByType(
                                      setFieldValue,
                                      'OTHER_INTEGRATION',
                                      values.terminals,
                                    );
                                  }}
                                  setFieldValue={setFieldValue}
                                  value="OTHER_INTEGRATION"
                                  customClass="px-0"
                                  currentComboCheckboxValue={values.electronicInvoicingType}
                                />
                                <RoundedCheckbox
                                  name="electronicInvoicingType"
                                  label="Sin terminal"
                                  onChange={(value) => {
                                    setFieldValue('electronicInvoicingType', value);
                                    setFieldValue('posSale', 'false');
                                    resetTerminalsByType(setFieldValue, 'NO_TERMINAL', values.terminals);
                                  }}
                                  setFieldValue={setFieldValue}
                                  value="NO_TERMINAL"
                                  customClass="px-0"
                                  currentComboCheckboxValue={values.electronicInvoicingType}
                                />
                              </div>
                            </div>
                            {values.electronicInvoicingType !== 'OTHER_INTEGRATION' && (
                              <CommerceElectronicInvoicing
                                electronicInvoicingFormRef={electronicInvoicingFormRef}
                                localState={localState}
                                electronicInvoiceFromApi={electronicInvoiceFromApi}
                                electronicInvoicingType={values.electronicInvoicingType}
                                digitalCertificateRequest={values.digitalCertificateRequest}
                                toggleDigitalCertificateRequest={() =>
                                  setFieldValue(
                                    'digitalCertificateRequest',
                                    !values.digitalCertificateRequest,
                                  )
                                }
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                    {values.commercialCenter === 'NO' &&
                      values.integration === INTEGRATION_NO &&
                      !values.wantsIntegratedPos && (
                        <Select
                          label="¿Venta con POS?"
                          className="col-12"
                          name="posSale"
                          required
                          disabled={values.electronicInvoicing}
                        >
                          <BooleanishOptions />
                        </Select>
                      )}
                    {values.commercialCenter === 'NO' &&
                    values.integration === INTEGRATION_NO &&
                    !values.wantsIntegratedPos &&
                    values.posSale === 'true' ? (
                      <NewGetnetTerminal
                        localState={localState}
                        terminals={terminals}
                        electronicInvoicingFormRef={electronicInvoicingFormRef}
                      />
                    ) : (
                      <>
                        <label className="label-text-editor">Comentarios</label>
                        <ReactQuill
                          theme="snow"
                          value={noPosSaleComments}
                          onChange={(event) => setNoPosSaleComments(event)}
                        />
                      </>
                    )}
                  </>
                )}
                <ZurichModal
                  show={showZurichModal}
                  toggleModal={() => setShowZurichModal(false)}
                  insurances={insurances}
                />
              </Form>
            );
          }}
        </Formik>
      </Container>
      <NavigationFooter previousHandler={handleGoBack} nextHandler={handleSubmit} loading={isLoading} />
    </>
  );
}

function NewGetnetTerminal({
  localState,
  terminals,
  electronicInvoicingFormRef,
}: {
  localState: any;
  terminals: CustomTableChunk[];
  electronicInvoicingFormRef: React.MutableRefObject<FormikProps<any> | null>;
}) {
  const { setFieldValue, values } = useFormikContext<any>();
  const priceDebounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const filteredTerminals: CustomTableChunk[] = (() => {
    if (!Array.isArray(terminals) || !terminals.length) return terminals;

    const [firstChunk, ...rest] = terminals;

    if (!values.electronicInvoicing || values.electronicInvoicingType === 'NO_TERMINAL') {
      return [
        {
          ...firstChunk,
          rows: firstChunk.rows.filter((row) => !row.electronicInvoicingAssociated),
        },
        ...rest,
      ];
    }

    if (values.electronicInvoicingType === 'GETNET_INTEGRATION') {
      return [
        {
          ...firstChunk,
          rows: firstChunk.rows.filter((row) => !row.electronicInvoicingAssociated || !row.otherIntegration),
        },
        ...rest,
      ];
    }

    if (values.electronicInvoicingType === 'OTHER_INTEGRATION') {
      return [
        {
          ...firstChunk,
          rows: firstChunk.rows.filter((row) => !row.electronicInvoicingAssociated || row.otherIntegration),
        },
        ...rest,
      ];
    }

    return terminals;
  })();

  const handleChangePersonalizedTable = (
    event: ChangeEvent<HTMLInputElement>,
    tableData: Array<CustomTableChunk>,
    setFieldValue: (field: string, value: CustomTableChunk[]) => void,
    field: string,
    column: BusinessTableColumn,
  ) => {
    const newTableData = [...tableData];
    const { value } = event.target;
    const id = event.target.id.split('_')[1];
    const indexInRows = newTableData[0].rows.findIndex((row) => row?.id?.toString() === id);

    newTableData[0].rows[indexInRows][column] = value;
    setFieldValue(field, newTableData);

    if (priceDebounceTimers.current[id]) clearTimeout(priceDebounceTimers.current[id]);

    const priceBusinessId = localState?.businessId || localState?.id;
    if (value && priceBusinessId && Number.isFinite(Number(priceBusinessId))) {
      priceDebounceTimers.current[id] = setTimeout(async () => {
        const priceRes = await getTerminalsPrice(id, value, String(priceBusinessId));
        if (priceRes?.data?.price != null) {
          newTableData[0].rows[indexInRows].deferred = priceRes.data.price;
          setFieldValue(field, [...newTableData]);
        }
      }, 400);
    }
  };

  return (
    <>
      <div className="col-12 mt-3 mb-4">
        <BorderedText
          text="Las terminales POS se debitarán en la liquidación de Getnet."
          icon={PosSettlementIcon}
          iconAlt="Liquidación Getnet"
        />
      </div>
      <h3 className="section-title mt-4">TERMINAL GETNET</h3>
      <PersonalaizedTariffTable
        setFieldValue={setFieldValue}
        headings={terminalHeadings}
        tableData={filteredTerminals}
        field="terminals"
        handleChangePersonalaizedTable={handleChangePersonalizedTable}
      />
      <div className="col-12 mt-3 mb-4">
        <BorderedText
          text="Promoción que aplica para el/los POS seleccionado/s"
          icon={PosSettingsIcon}
          iconAlt="Promoción"
        />
      </div>
      <ShippingSection localState={localState} />
    </>
  );
}

function SameAsMainAddressSwitch({ localState }: { localState: any }) {
  const { setFieldValue } = useFormikContext();

  const handleFillWithMainAddressData = () => {
    sameAsMainAddress = !sameAsMainAddress;

    setFieldValue('stateId', sameAsMainAddress ? localState.city?.stateId! : '');
    setFieldValue('cityId', sameAsMainAddress ? localState.city?.id! : '');
    setFieldValue('addressStreet', sameAsMainAddress ? localState.addressStreet : '');
    setFieldValue('addressNumber', sameAsMainAddress ? localState.addressNumber! : '');
    setFieldValue('addressFlatNumber', sameAsMainAddress ? localState.addressFlatNumber : '');
    setFieldValue('addressSolar', sameAsMainAddress ? localState.addressSolar : '');
    setFieldValue('addressPlot', sameAsMainAddress ? localState.addressPlot : '');
  };

  return (
    <div className="input col-md-12 checkbox-label form-check form-switch">
      <label>
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          name="sameAsBusinessInfo"
          onChange={() => handleFillWithMainAddressData()}
        />
        Dirección igual al Domicilio Principal
      </label>
    </div>
  );
}

function PaymentProcessorGroup() {
  const { setFieldValue, values } = useFormikContext<{ paymentProcessor: string }>();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    setFieldValue('paymentProcessor', checked ? value : '');
  };

  const commonProps = { name: 'paymentProcessor', groupValue: values.paymentProcessor, onChange };

  return (
    <div className="col-md-12">
      <p className="subtitle mb-2">¿Tienes pasarela de pago?</p>
      <div className="col-12 d-flex gap-4">
        <PaymentProcessorCheckbox {...commonProps} label="Plexo" value={ECommerceProcessorEnum.PLEXO} />
        <PaymentProcessorCheckbox {...commonProps} label="Bamboo" value={ECommerceProcessorEnum.BAMBOO} />
        <PaymentProcessorCheckbox {...commonProps} label="SEP" value={ECommerceProcessorEnum.OTRO} />
        <PaymentProcessorCheckbox {...commonProps} label="PayExpress" value={ECommerceProcessorEnum.PAYEXPRESS} />
      </div>
    </div>
  );
}

function PaymentProcessorCheckbox({
  label,
  groupValue,
  ...props
}: ComponentProps<'input'> & { label: string; groupValue?: string }) {
  return (
    <div className={`form-check form-check-inline px-0`}>
      <label className="form-check-label">
        <input
          className="form-check-input radio-button"
          type="checkbox"
          checked={props.value && groupValue ? props.value === groupValue : props.checked}
          {...props}
        />
        {label}
      </label>
    </div>
  );
}

function IntegrationTypeGroup() {
  const { setFieldValue, values } = useFormikContext<{ integrationType: any[] }>();

  const handleCheckboxIntegrationType = (value: number) => {
    const newValue = values.integrationType.includes(value)
      ? values.integrationType.filter((id: number) => id !== value)
      : [...values.integrationType, value];

    setFieldValue('integrationType', newValue);
  };

  return (
    <>
      <p className="subtitle mt-4 mb-2">¿Va a adquirir alguno de los siguientes productos?</p>
      <div className="col-12 d-flex gap-4 mb-4">
        {integrationType.map((option) => (
          <label key={option.name} className="checkbox-label align-items-center mt-1">
            <Field
              className="checkbox"
              type="checkbox"
              name="integrationType"
              checked={values?.integrationType?.includes(option.value)}
              onChange={() => handleCheckboxIntegrationType(option.value)}
            />
            {option.name}
          </label>
        ))}
      </div>
    </>
  );
}

function ShippingSection({ localState }: { localState: any }) {
  const { setFieldValue, values } = useFormikContext<any>();

  return (
    <>
      <h3 className="section-title mt-4">FORMA DE ENTREGA</h3>
      <div className="col-md-4">
        <RoundedCheckbox
          name="shippingType"
          label="Getnet"
          onChange={(value) => setFieldValue('shippingType', value)}
          setFieldValue={setFieldValue}
          value={ShippingTypeEnum.NAD}
          customClass="px-0"
          currentComboCheckboxValue={values.shippingType}
        />
        <RoundedCheckbox
          name="shippingType"
          label="Canal de ventas"
          onChange={(value) => setFieldValue('shippingType', value)}
          setFieldValue={setFieldValue}
          value={ShippingTypeEnum.SALES_CHANNEL}
          currentComboCheckboxValue={values.shippingType}
        />
      </div>
      {ShippingTypeEnum.NAD === values.shippingType && (
        <>
          <Switch
            htmlFor="withdrawInGetnet"
            id="withdrawInGetnet_switch"
            label="Retirar producto en Getnet"
            handleOnClick={() => setFieldValue('withdrawInGetnet', !values.withdrawInGetnet)}
            name="withdrawInGetnet"
            containerClass="col-md-12"
            checked={values.withdrawInGetnet}
          />
          {values.withdrawInGetnet ? (
            <InfoBlock
              title="OFICINAS DE GETNET"
              fields={[
                {
                  name: 'Dirección',
                  value: 'La Cumparsita 1475, Piso 6 | Edificio Plaza Alemania',
                },
                { name: 'Horarios', value: '9:00 a 18:00hs' },
              ]}
            />
          ) : (
            <>
              <SameAsMainAddressSwitch localState={localState} />

              <AddressStateSelect disabled={sameAsMainAddress} />
              <AddressCitySelect localState={localState} disabled={sameAsMainAddress} />
              <Input
                className="input col-12"
                type="text"
                name="addressStreet"
                label="Calle"
                disabled={sameAsMainAddress}
                required={!values.withdrawInGetnet}
              />
              <Input
                className="input col-md-3"
                type="text"
                name="addressNumber"
                label="N° de puerta"
                disabled={sameAsMainAddress}
                required={!values.withdrawInGetnet}
              />
              <Input
                className="input col-md-3"
                type="number"
                name="addressFlatNumber"
                label="Apto (Opcional)"
                disabled={sameAsMainAddress}
              />
              <Input
                className="input col-md-3"
                type="number"
                name="addressSolar"
                label="Solar (Opcional)"
                disabled={sameAsMainAddress}
              />
              <Input
                className="input col-md-3"
                type="number"
                name="addressPlot"
                disabled={sameAsMainAddress}
                label="Manzana (Opcional)"
              />

              <Select className="col-md-6 mt-6" label="Horario" name="deliveryTime" required>
                <option value="10-18hs">10 a 18 hs</option>
                <option value="10-12hs">10 a 12 hs</option>
                <option value="15-18hs">15 a 18 hs</option>
              </Select>
              <Input
                className="input col-md-6"
                type="tel"
                name="phoneNumber"
                label="Télefono de contacto"
                required={values.shippingType === ShippingTypeEnum.NAD}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

function ApplicantCityEffect({
  states,
  onCitiesLoaded,
}: {
  states: State[];
  onCitiesLoaded: (cities: City[]) => void;
}) {
  const { values } = useFormikContext<any>();

  useEffect(() => {
    if (!values.applicantDepartment || !states.length) return;
    const matchingState = states.find((s) => s.name === values.applicantDepartment);
    if (!matchingState) return;
    getCities(matchingState.id)
      .then((res) => onCitiesLoaded(res.data))
      .catch(() => onCitiesLoaded([]));
  }, [values.applicantDepartment, states]);

  return null;
}

function CommerceElectronicInvoicing({
  electronicInvoicingFormRef,
  localState,
  electronicInvoiceFromApi,
  electronicInvoicingType,
  digitalCertificateRequest,
  toggleDigitalCertificateRequest,
}: {
  electronicInvoicingFormRef: React.MutableRefObject<FormikProps<any> | null>;
  localState: any;
  electronicInvoiceFromApi: Record<string, unknown> | null;
  electronicInvoicingType: string;
  digitalCertificateRequest: boolean | null | undefined;
  toggleDigitalCertificateRequest: () => void;
}) {
  const [applicantStates, setApplicantStates] = useState<State[]>([]);
  const [applicantCities, setApplicantCities] = useState<City[]>([]);
  const planTypeRef = useRef<string | null>(
    normalizePlanTypeFromApi(
      pickPlanTypeFromElectronicInvoice(electronicInvoiceFromApi as Record<string, unknown>) ??
        (localState?.planType as unknown),
    ),
  );

  useEffect(() => {
    getStates()
      .then(setApplicantStates)
      .catch(() => setApplicantStates([]));
  }, []);

  const electronicInvoiceApiEntriesWithoutDgiUser = electronicInvoiceFromApi
    ? Object.entries(electronicInvoiceFromApi).filter(
        ([key]) =>
          key !== 'type' && key !== 'dgiUserRut' && key !== 'dgiUserDocument' && key !== 'dgiUserKey',
      )
    : [];

  const handleSubmitElectronicInvoicing = async (values: any) => {
    const { type: _dropType, ...rest } = values;
    const basePayload = nullifyCertificateRegistrationFieldsInPayload(
      { ...rest, type: electronicInvoicingType } as Record<string, unknown>,
      values.digitalCertificateRequest === true,
    );
    const payload =
      electronicInvoicingType === 'NO_TERMINAL' ? basePayload : { ...basePayload, planType: null };
    const isUpdate =
      electronicInvoiceFromApi !== null && electronicInvoicePayloadHasAnyValue(electronicInvoiceFromApi);
    const response = await saveElectronicInvoice(payload, localState.businessId || localState.id, isUpdate);

    if (!response.result) {
      triggerToasts('Error al guardar los datos de facturación electrónica');
    }
  };

  return (
    <Formik
      innerRef={electronicInvoicingFormRef}
      enableReinitialize
      initialValues={{
        ...(digitalCertificateRequest === true
          ? {
              ...emptyCertificateRegistrationFields(),
              ...(electronicInvoiceApiEntriesWithoutDgiUser.length
                ? Object.fromEntries(
                    electronicInvoiceApiEntriesWithoutDgiUser.map(([k, v]) => {
                      if (k === 'certificateDuration') return [k, normalizeCertificateDurationFromApi(v)];
                      if (k === 'planType') return [k, normalizePlanTypeFromApi(v)];
                      return [k, v];
                    }),
                  )
                : {}),
            }
          : emptyCertificateRegistrationFields()),
        dgiUserRut: (electronicInvoiceFromApi?.dgiUserRut as string) ?? localState?.rut ?? '',
        dgiUserDocument: (electronicInvoiceFromApi?.dgiUserDocument as string) ?? '',
        dgiUserKey: (electronicInvoiceFromApi?.dgiUserKey as string) ?? '',
        digitalCertificateRequest: digitalCertificateRequest === true,
        planType: planTypeRef.current,
      }}
      validationSchema={schemas.buildCommerceElectronicInvoicingSchema(electronicInvoicingType)}
      onSubmit={handleSubmitElectronicInvoicing}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form>
          <ElectronicInvoicePlanTypeEffect invoicingType={electronicInvoicingType} />
          {electronicInvoicingType === 'NO_TERMINAL' && (
            <div className="mb-4">
              <h3 className="subtitle mb-3">Tipo de plan</h3>
              <hr style={{ marginBottom: '1rem', borderTop: '1px solid #e0e0e0' }} />
              <div className="d-flex gap-4 flex-wrap align-items-start">
                <div className="form-check form-check-inline px-0">
                  <input
                    id="plan-type-lite"
                    type="radio"
                    className="form-check-input radio-button"
                    name="planTypeTaface"
                    checked={values.planType === TafaceTypePlanEnum.LITE}
                    onChange={() => setFieldValue('planType', TafaceTypePlanEnum.LITE)}
                  />
                  <label className="form-check-label ms-1" htmlFor="plan-type-lite">
                    Plan Lite (Hasta 30 facturas mensuales)
                  </label>
                </div>
                <div className="form-check form-check-inline px-0">
                  <input
                    id="plan-type-premium"
                    type="radio"
                    className="form-check-input radio-button"
                    name="planTypeTaface"
                    checked={values.planType === TafaceTypePlanEnum.PREMIUM}
                    onChange={() => setFieldValue('planType', TafaceTypePlanEnum.PREMIUM)}
                  />
                  <label className="form-check-label ms-1" htmlFor="plan-type-premium">
                    Plan Premium (Hasta 100 facturas mensuales)
                  </label>
                </div>
              </div>
              {errors.planType && touched.planType && (
                <span className="text-error form-text d-block mt-1">{String(errors.planType)}</span>
              )}
            </div>
          )}
          <div className="mt-4 mb-2">
            <h4 className="subtitle mb-3">Usuario DGI para e-Factura</h4>
            <hr style={{ marginBottom: '1rem', borderTop: '1px solid #e0e0e0' }} />
          </div>
          <Row>
            <Input className="input col-md-4" type="text" name="dgiUserRut" label="RUT" disabled />
            <Input className="input col-md-4" type="text" name="dgiUserDocument" label="Cédula" />
            <Input className="input col-md-4" type="text" name="dgiUserKey" label="Clave" />
          </Row>
          <Switch
            htmlFor="digitalCertificateRequest_switch"
            id="digitalCertificateRequest_switch"
            label="Solicitud de certificado digital"
            handleOnClick={() => {
              const next = !values.digitalCertificateRequest;
              const dgiUserRut = values.dgiUserRut;
              const dgiUserDocument = values.dgiUserDocument;
              const dgiUserKey = values.dgiUserKey;
              planTypeRef.current = values.planType;
              toggleDigitalCertificateRequest();
              setFieldValue('digitalCertificateRequest', next);
              if (!next) {
                clearElectronicInvoiceCertificateBlockFields(setFieldValue);
                queueMicrotask(() => {
                  setFieldValue('dgiUserRut', dgiUserRut);
                  setFieldValue('dgiUserDocument', dgiUserDocument);
                  setFieldValue('dgiUserKey', dgiUserKey);
                });
              } else {
                restoreElectronicInvoiceCertificateBlockFromApi(electronicInvoiceFromApi, setFieldValue);
              }
            }}
            name="digitalCertificateRequest"
            containerClass="col-md-12 mt-0"
            checked={values.digitalCertificateRequest === true}
          />
          {values.digitalCertificateRequest === true && (
            <>
              <div className="mt-4">
                <h3 className="section-title mt-4">Registro para Facturación Electrónica de Getnet</h3>
                <h4 className="subtitle mb-3">Datos de la empresa</h4>
                <hr style={{ marginBottom: '1rem', borderTop: '1px solid #e0e0e0' }} />
                <Row>
                  <div className="input col-md-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="mb-0">
                        Código Sucursal Principal (DGI)
                        <span className="required-asterisk"> *</span>
                      </label>
                      <a
                        href="https://www.youtube.com/watch?v=oAbrbJr2OVk"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-underline"
                        style={{
                          color: '#dc3545',
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                          textDecoration: 'underline',
                        }}
                      >
                        ¿Dónde se obtiene?
                      </a>
                    </div>
                    <Input name="dgiCode" required type="number" />
                  </div>
                  <Select className="col-md-4" name="businessType" label="Tipo de Negocio" required>
                    {tafaceBusinessType.map((tafaceBusinessType: InfoBlockField) => (
                      <option key={tafaceBusinessType.value} value={tafaceBusinessType.value}>
                        {tafaceBusinessType.name}
                      </option>
                    ))}
                  </Select>
                  <Select className="col-md-4" name="companyType" label="Tipo de empresa" required>
                    {tafaceCompanyType.map((item: InfoBlockField) => (
                      <option key={item.value} value={item.value}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </Row>
              </div>
              <div className="mt-4 mb-4">
                <h4 className="subtitle mb-3">Datos para certificado</h4>
                <hr style={{ marginBottom: '1rem', borderTop: '1px solid #e0e0e0' }} />
                <ul className="mb-0" style={{ paddingLeft: '20px', fontSize: '0.9rem', color: '#666666' }}>
                  <li className="mb-2">
                    La persona indicada debe ser la misma que pague, presente la documentación en ABITAB y
                    cuya firma certifique el Escribano.
                  </li>
                  <li className="mb-2">
                    Se recomienda <strong>solicitar el certificado por 2 años</strong>, ya que el costo
                    notarial es el mismo que por 1 año.
                  </li>
                  <li>
                    <strong>El mail ingresado deberá coincidir con el mail certificado notarialmente</strong>{' '}
                    y será el que utilice ABITAB.
                  </li>
                </ul>
              </div>
              <Row>
                <Select
                  className="input col-md-4"
                  name="certificateDuration"
                  label="Duración del Certificado Digital"
                  required
                >
                  {tafaceDigitalCertificate.map((opt: InfoBlockField) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.name}
                    </option>
                  ))}
                </Select>
                <Input
                  className="input col-md-4"
                  type="email"
                  name="contactEmail"
                  label="Email de contacto"
                  required
                />
                <Select
                  className="col-md-4"
                  name="documentType"
                  label="Tipo de Documento"
                  required
                  isNumericValue
                >
                  {documentTypes.map((documentType: InfoBlockField) => (
                    <option key={documentType.value} value={documentType.value}>
                      {documentType.name}
                    </option>
                  ))}
                </Select>
                <Input
                  className="input col-md-4"
                  type="text"
                  name="documentNumber"
                  label="Nro. de Documento"
                  placeholder="Sin puntos ni guiones"
                  required
                />
                <Input
                  className="input col-md-4"
                  type="text"
                  name="firstName"
                  label="Primer Nombre"
                  required
                />
                <Input className="input col-md-4" type="text" name="secondName" label="Segundo Nombre" />
                <Input
                  className="input col-md-4"
                  type="text"
                  name="firstLastName"
                  label="Primer Apellido"
                  required
                />
                <Input
                  className="input col-md-4"
                  type="text"
                  name="secondLastName"
                  label="Segundo Apellido"
                  required
                />
                <Input className="input col-md-4" type="tel" name="phone" label="Teléfono" required />
                <Input
                  className="input col-12"
                  type="text"
                  name="applicantAddress"
                  label="Dirección del Solicitante"
                  required
                />
                <ApplicantCityEffect states={applicantStates} onCitiesLoaded={setApplicantCities} />
                <Select
                  className="col-md-6"
                  name="applicantDepartment"
                  label="Departamento del Solicitante"
                  required
                  handleOnChange={(targetValue) => {
                    setFieldValue('applicantDepartment', targetValue);
                    setFieldValue('applicantCity', '');
                  }}
                >
                  {applicantStates.map((state, index) => (
                    <option key={index} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </Select>
                <Select
                  className="col-md-6"
                  name="applicantCity"
                  label="Ciudad del Solicitante"
                  required
                  disabled={!values.applicantDepartment}
                >
                  {applicantCities.map((city, index) => (
                    <option key={index} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </Select>
              </Row>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
}

export default CommerceTerminals;
